import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { ApiClientQueryParams, ApiClientResponse } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import InstagramPostsStore from "../../stores/InstagramPostsStore";
import InstagramPostsResponseEntity from "./entities/InstagramPostsResponseEntity";
import InstagramPostsServiceInterface from "./InstagramPostsServiceInterface";
import InstagramPostsResponseSchema from "./schemas/InstagramPostsResponseSchema";

export interface GetPostsRequestParamsDTO {
    galleryId?: number;
    productIds?: number[];
    perPage: number;
    offset?: number;
    sortBy?: "newest_first" | "tagged_first";
    filter?: "all" | "only_tagged";
    postMediaFilter: "all" | "only_tagged";
}

@singleton()
export class InstagramPostsService implements InstagramPostsServiceInterface {
    constructor(
        private instagramPostsStore: InstagramPostsStore,
        private readonly apiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}
    makeRequest(
        reqParams: GetPostsRequestParamsDTO,
        reqStatus: STATUS
    ): Observable<ApiClientResponse<unknown>> {
        this.logger.debug(
            "InstagramPostsService.getPosts is started",
            reqParams
        );
        this.instagramPostsStore.setStatus(reqStatus);

        const queryParams: ApiClientQueryParams = {
            perPage: reqParams.perPage,
            postMediaFilter: reqParams.postMediaFilter,
        };
        if (reqParams.galleryId) {
            queryParams.galleryId = reqParams.galleryId;
        }
        if (reqParams.sortBy) {
            queryParams.sortBy = reqParams.sortBy;
        }
        if (reqParams.filter) {
            queryParams.filter = reqParams.filter;
        }
        if (reqParams.offset) {
            queryParams.offset = reqParams.offset;
        }
        return this.apiClient.get({
            url: "/instagram/getGalleryPosts",
            responseType: "json",
            queryParams: queryParams,
        });
    }

    getPosts(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<InstagramPostsResponseEntity> {
        return this.makeRequest(reqParams, STATUS.INITIAL_LOADING).pipe(
            finalize(() => {
                this.logger.debug("InstagramPostsService.getPosts finalized");
            }),
            map((v) => create(v.body, InstagramPostsResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "InstagramPostsService.getPosts is errored",
                    reqParams
                );
                this.instagramPostsStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((v) => {
                applyTransaction(() => {
                    this.instagramPostsStore.set(v.items);
                    this.instagramPostsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                            page: 1,
                        },
                    }));

                    this.instagramPostsStore.setStatus(STATUS.READY);

                    this.logger.debug(
                        "InstagramPostsService.getPosts is ready",
                        reqParams
                    );
                });
            })
        );
    }

    loadMorePosts(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<InstagramPostsResponseEntity> {
        return this.makeRequest(reqParams, STATUS.LOADING).pipe(
            map((v) => create(v.body, InstagramPostsResponseSchema)),
            catchError((error: unknown) => {
                this.logger.debug(
                    "InstagramPostsService.loadMorePosts is errored",
                    reqParams
                );
                this.instagramPostsStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((v) => {
                applyTransaction(() => {
                    this.instagramPostsStore.add(v.items);
                    this.instagramPostsStore.update((state) => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                            page: state.paginationMeta.page + 1,
                        },
                    }));

                    this.instagramPostsStore.setStatus(STATUS.READY);

                    this.logger.debug(
                        "InstagramPostsService.loadMorePosts is ready",
                        reqParams
                    );
                });
            })
        );
    }

    // Tagged Posts
    makeTaggedPostsRequest(
        reqParams: GetPostsRequestParamsDTO,
        reqStatus: STATUS
    ): Observable<InstagramPostsResponseEntity> {
        this.logger.debug(
            "InstagramPostsService.getPosts is started",
            reqParams
        );
        this.instagramPostsStore.setStatus(reqStatus);
        const queryParams: ApiClientQueryParams = {
            perPage: reqParams.perPage,
            postMediaFilter: reqParams.postMediaFilter,
        };
        if (reqParams.galleryId) {
            queryParams.galleryId = reqParams.galleryId;
        }
        if (reqParams.productIds && reqParams.productIds.length) {
            reqParams.productIds.forEach((productId, idx) => {
                queryParams[`productIds[${idx}]`] = productId;
            });
        }
        if (reqParams.offset) {
            queryParams.offset = reqParams.offset;
        }
        return this.apiClient
            .get({
                url: "/instagram/getTaggedPosts",
                responseType: "json",
                queryParams: queryParams,
            })
            .pipe(
                finalize(() => {
                    this.instagramPostsStore.setStatus(STATUS.READY);
                }),
                map((v) => create(v.body, InstagramPostsResponseSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "InstagramPostsService.getTaggedPosts is errored",
                        reqParams
                    );
                    this.instagramPostsStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "InstagramPostsService.getTaggedPosts is ready",
                        reqParams
                    );
                })
            );
    }

    getTaggedPosts(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<InstagramPostsResponseEntity> {
        return this.makeTaggedPostsRequest(
            reqParams,
            STATUS.INITIAL_LOADING
        ).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.instagramPostsStore.set(v.items);
                    this.instagramPostsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                            page: 1,
                        },
                    }));
                });
            })
        );
    }

    loadMoreTaggedPosts(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<InstagramPostsResponseEntity> {
        return this.makeTaggedPostsRequest(reqParams, STATUS.LOADING).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.instagramPostsStore.add(v.items);
                    this.instagramPostsStore.update((state) => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                            page: state.paginationMeta.page + 1,
                        },
                    }));
                });
            })
        );
    }
}
export default InstagramPostsService;
